import { resolveFieldLabel } from '@/features/client-portal/review/section-detail-configs'
import { formatCode } from '@/shared/constants/config'
import type { AuditLogEntry } from './models/audit-log'

/** Tarjeta de metadata destacada bajo un evento (motivo, comentario, corrección…). */
export type AuditCard = {
  /** Encabezado corto de la tarjeta (opcional). */
  title?: string
  /** Cuerpo de la tarjeta. */
  body: string
  /** Color de la tarjeta según la naturaleza del dato. */
  tone?: 'neutral' | 'info' | 'warning' | 'danger'
}

export type HumanAuditEntry = {
  /** Etiqueta corta de estado, coloreada (heading): "Aprobado", "Sección guardada"… */
  label: string
  /**
   * Frase verbal SIN sujeto ("guardó Datos personales", "solicitó 2 correcciones"). El sujeto
   * (el nombre del actor) lo antepone el timeline en negrita, tejido en la oración — así no se
   * repite el actor como en los títulos auto-referenciales previos ("El cliente guardó…").
   */
  predicate: string
  /**
   * Si es `true`, el evento NO tiene un sujeto-persona (p.ej. "El enlace expiró"): el timeline
   * muestra el predicado como oración completa, sin anteponer el nombre.
   */
  subjectless?: boolean
  /** Línea secundaria muted (antes → después, lista de campos…). */
  detail?: string
  /** Tarjetas de metadata enriquecida (comentarios de corrección, motivos…). */
  cards?: AuditCard[]
  /** Nombre base del actor (interno resuelto por id, o "Cliente"/"Sistema"). */
  actor: string
  /** Naturaleza del actor (para el avatar y para inyectar la identidad del cliente). */
  actorType: 'client' | 'internal' | 'system'
  occurredAt: string
}

/**
 * Traductor de CLAVES TÉCNICAS a los nombres que la persona lee en pantalla.
 *
 * ── POR QUÉ EXISTE ───────────────────────────────────────────────────────────
 *
 * La bitácora vieja —todo lo escrito ANTES de que la API sellara las etiquetas— guarda sólo
 * claves: `initial_contact`, `resolutionResult`, `in_review`. El humanizador caía a ellas y el
 * renglón terminaba diciendo «Etapa initial_contact», que es el modelo de datos filtrándose al
 * usuario final. La etiqueta no se puede inventar desde acá: vive en el expediente abierto, así
 * que quien pinta la bitácora inyecta su propio diccionario, exactamente igual que inyecta
 * `resolveActor`.
 *
 * ── EL CONTRATO ──────────────────────────────────────────────────────────────
 *
 * Cada método devuelve `undefined` cuando NO puede resolver. El humanizador cae entonces a un
 * texto genérico y digno («una etapa», «un campo») — **nunca a la clave**. Todo es opcional: sin
 * diccionario la bitácora se sigue leyendo, sólo que más genérica.
 *
 * ⚠️ La clave de un campo llega en DOS espacios: pelada (`resolutionResult`, lo que sella el
 * escritor del campo) o como TOKEN de variable (`etapa4.resolutionResult`, con el que las reglas
 * del kernel nombran a sus campos). Resolver ambos es responsabilidad de quien arma el diccionario.
 */
export type AuditLabelResolver = {
  /** Nombre de una etapa por su clave. */
  stage?: (key: string) => string | undefined
  /** Etiqueta de un punto por su clave (la etapa acota la búsqueda cuando la entrada la trae). */
  item?: (key: string, stageKey?: string) => string | undefined
  /** Etiqueta de un campo por su clave pelada o por su token de variable. */
  field?: (key: string, stageKey?: string, itemKey?: string) => string | undefined
  /**
   * Etiqueta de un VALOR de campo: `in_review` → «En revisión», `true` → «Sí».
   *
   * Sólo una LISTA y una casilla guardan claves; el resto de los valores ya vienen escritos para
   * una persona desde la API (`formatFieldValue`) y no hay nada que traducir. `undefined` = una
   * opción que el expediente ya no declara; quien llama se queda con el valor tal cual, que es un
   * DATO —lo que la persona eligió—, no un identificador de la plataforma.
   */
  option?: (
    value: string,
    fieldKey: string,
    stageKey?: string,
    itemKey?: string
  ) => string | undefined
}

/** Sin diccionario: todo cae al texto genérico. */
const NO_LABELS: AuditLabelResolver = {}

/** Nombre legible de una sección del expediente. */
const SECTION_LABELS: Record<string, string> = {
  applicant: 'Datos personales',
  contact: 'Contacto',
  pep: 'Declaración PEP',
  economic: 'Información económica',
  equity: 'Estado patrimonial',
  legal_representatives: 'Representante legal',
  company: 'Datos de la empresa',
  attachments: 'Documentos'
}

/**
 * Nombre de una sección. Una clave que el catálogo no conoce —una sección nueva del portal que
 * todavía no se mapeó— cae a «una sección»: mostrar `legal_representatives` es enseñar el modelo
 * de datos, y el renglón se entiende igual sin nombrarla.
 */
const sectionLabel = (section: unknown): string =>
  typeof section === 'string' ? (SECTION_LABELS[section] ?? 'una sección') : 'una sección'

/** Etiqueta corta de estado (heading coloreado), estilo aprobación de documentos. */
const LABELS: Record<string, string> = {
  'portal.created': 'Creado',
  'portal.sent': 'Enviado',
  'portal.email_opened': 'Correo abierto',
  'portal.opened': 'Formulario abierto',
  'portal.section_saved': 'Sección guardada',
  'portal.submitted': 'Enviado a revisión',
  'portal.resubmitted': 'Reenviado',
  'portal.review_taken_over': 'Revisión tomada',
  'portal.review_started': 'Revisión iniciada',
  'portal.fields_reviewed': 'Campos revisados',
  'portal.field_edited': 'Campo corregido',
  'portal.changes_requested': 'Correcciones solicitadas',
  'portal.client_comment': 'Comentario',
  'portal.approved': 'Aprobado',
  'portal.rejected': 'Rechazado',
  'portal.revoked': 'Cancelado',
  'portal.reminder_sent': 'Recordatorio',
  'portal.expired': 'Expirado',
  'portal.link_extended': 'Enlace renovado',
  'portal.link_resent': 'Correo reenviado',
  'credit.started': 'Expediente iniciado',
  'credit.item_checked': 'Punto actualizado',
  'credit.field_answered': 'Respuesta guardada',
  'credit.stage_advanced': 'Etapa avanzada',
  'credit.financing_type_set': 'Modalidad actualizada',
  'credit.approved': 'Crédito aprobado',
  'credit.notice_skipped': 'Aviso omitido',
  'credit.notice_sent': 'Aviso enviado',
  'credit.notice_failed': 'Aviso fallido',
  'client.created': 'Cliente creado',
  'client.updated': 'Cliente actualizado',
  'client.deleted': 'Cliente eliminado',
  'negotiation.created': 'Negociación creada',
  'negotiation.updated': 'Negociación actualizada',
  'negotiation.state_changed': 'Estado cambiado',
  'negotiation.activated': 'Activada',
  'negotiation.deactivated': 'Desactivada',
  'negotiation.rescinded': 'Rescindida',
  'negotiation.rejected': 'Rechazada',
  'negotiation.deleted': 'Eliminada',
  'user.created': 'Usuario creado',
  'user.updated': 'Usuario actualizado',
  'user.deleted': 'Usuario eliminado'
}

/**
 * Etiqueta de un campo del portal, o `null` si el catálogo no lo describe.
 *
 * ☠️ `resolveFieldLabel` **cae al PATH crudo** cuando no reconoce el campo (`monthlyIncomes.0.x`)
 * — es su contrato y sirve a otros consumidores, pero acá ese path viajaría directo al renglón de
 * la bitácora. Devolver el mismo string que entró es la señal EXACTA de «no resolví»: se traduce a
 * `null` y quien llama decide su fallback.
 */
const portalFieldLabel = (field: string, section?: string): string | null => {
  if (!field) return null
  const label = resolveFieldLabel(field, section)
  return label === field ? null : label
}

/**
 * Etiqueta de un campo dentro de una revisión. El centinela `__section__` significa que se
 * marcó la sección entera (no un campo puntual): se muestra amigable en vez del string crudo.
 */
const reviewFieldLabel = (field: string): string | null =>
  field === '__section__' ? 'la sección completa' : portalFieldLabel(field)

const stringify = (value: unknown): string => {
  if (value == null || value === '') return '—'
  if (Array.isArray(value)) return value.join(', ')
  return String(value)
}

/** Etiqueta de un campo en corrección: sección + nombre (o sólo sección si es a nivel sección). */
function correctionLabel(c: { field?: string; section?: string }): string {
  const section = c.section ? sectionLabel(c.section) : ''
  if (!c.field || c.field === '__section__') return section || 'Sección completa'
  // Un campo que el catálogo no describe se queda con el nombre de su sección: el encabezado de
  // la tarjeta ubica la corrección igual, y un dot-path crudo de título no ubica nada.
  const field = portalFieldLabel(c.field, c.section)
  if (!field) return section || 'Corrección'
  return section ? `${section} · ${field}` : field
}

type Correction = { field?: string; section?: string; comment?: string | null }

/** Un texto de la metadata, o `null`. La metadata es `Mixed`: nada garantiza el tipo. */
const text = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value.trim() : null

/** La modalidad, por su CÓDIGO estable. `null` cuando la entrada no la trae. */
function financingTypeLabel(value: unknown): string | null {
  if (value === 'cash') return 'Contado'
  if (value === 'credit') return 'Crédito'
  return null
}

/**
 * El detalle del arranque: contra qué negociación y con qué modalidad nació el expediente.
 *
 * Todo es opcional a propósito — una entrada vieja que no traiga alguna clave muestra el resto, y
 * una que no traiga ninguna se queda sin detalle en vez de mostrar «N/A · —».
 */
function creditStartDetail(meta: Record<string, unknown>): string | undefined {
  const parts: string[] = []
  if (meta.negotiationCode != null) {
    parts.push(`Negociación ${formatCode(meta.negotiationCode as number, 'negotiation')}`)
  }
  const financingType = financingTypeLabel(meta.financingType)
  parts.push(financingType ? `Modalidad: ${financingType}` : 'Modalidad por definir')
  return parts.join(' · ')
}

/* ─────────────── Las tres claves del expediente, traducidas o calladas ─────────────── */

/** `stageKey`/`itemKey` de la entrada, listos para pasarle al diccionario. */
const keyOf = (meta: Record<string, unknown>, key: string): string | undefined =>
  text(meta[key]) ?? undefined

/**
 * El nombre de una etapa: PRIMERO lo que la API selló, y sólo si falta se traduce la clave contra
 * el expediente abierto. El orden importa — el sello es lo que se veía el día del hecho; el
 * diccionario es el estado de HOY, que pudo haber cambiado de nombre.
 */
function stageName(
  meta: Record<string, unknown>,
  labels: AuditLabelResolver,
  labelKey = 'stageLabel',
  keyKey = 'stageKey'
): string | null {
  const sealed = text(meta[labelKey])
  if (sealed) return sealed
  const key = keyOf(meta, keyKey)
  return (key && labels.stage?.(key)) || null
}

/** La etiqueta del punto de la entrada: sello primero, diccionario después. */
function itemName(meta: Record<string, unknown>, labels: AuditLabelResolver): string | null {
  const sealed = text(meta.itemLabel)
  if (sealed) return sealed
  const key = keyOf(meta, 'itemKey')
  return (key && labels.item?.(key, keyOf(meta, 'stageKey'))) || null
}

/** La etiqueta del campo de la entrada: sello primero, diccionario después. */
function fieldName(meta: Record<string, unknown>, labels: AuditLabelResolver): string | null {
  const sealed = text(meta.fieldLabel)
  if (sealed) return sealed
  const key = keyOf(meta, 'fieldKey')
  return (key && labels.field?.(key, keyOf(meta, 'stageKey'), keyOf(meta, 'itemKey'))) || null
}

/**
 * LA RESPUESTA que se guardó, legible.
 *
 * La API ya escribe el valor «para una persona» (`formatFieldValue`): una fecha, un monto con su
 * moneda, «1 archivo». La ÚNICA excepción son la lista y la casilla, que guardan la CLAVE de la
 * opción (`in_review`, `true`) — y eso sí es modelo de datos. El diccionario la traduce; si no
 * puede, se muestra el valor tal cual, porque es lo que la persona eligió y no un identificador
 * de la plataforma.
 */
function answerText(meta: Record<string, unknown>, labels: AuditLabelResolver): string | undefined {
  const formatted = text(meta.formatted)
  if (formatted) return formatted
  if (meta.value == null || meta.value === '') return undefined

  const fieldKey = keyOf(meta, 'fieldKey')
  const named = (value: string): string =>
    (fieldKey &&
      labels.option?.(value, fieldKey, keyOf(meta, 'stageKey'), keyOf(meta, 'itemKey'))) ||
    value

  if (typeof meta.value === 'string') return named(meta.value)
  if (Array.isArray(meta.value)) return meta.value.map((v) => named(String(v))).join(', ')
  return stringify(meta.value)
}

/**
 * CUÁL aviso, en las palabras que se leyeron en el correo.
 *
 * Sale de `itemLabel`, que la API sella al escribir. Las entradas ANTERIORES a ese sello no lo
 * traen y devuelven `null`: quien las muestra cae a su texto genérico, que es exactamente lo que
 * decían antes. Nunca se cae a `template` — es un identificador técnico (`credit-approved`), y
 * enseñarlo como nombre del aviso es peor que no nombrarlo.
 *
 * ☠️ **Tampoco se cae al PUNTO** (`labels.item(itemKey)`), aunque la clave esté ahí y resuelva.
 * Un aviso con nombre propio viaja como `rule.label || item.label` (`item-effects.ts`): cuando la
 * regla SÍ tiene nombre, el punto se llama distinto que el correo, y titular el renglón «el aviso
 * “Seguimiento de la resolución”» cuando el asunto decía «Resolución aprobada · aviso al cliente»
 * es afirmar algo falso. El punto es un dato cierto, así que va al DETALLE —«Punto …»—, que es
 * donde ubica sin mentir.
 */
function noticeName(meta: Record<string, unknown>): string | null {
  const label = text(meta.itemLabel)
  return label ? `el aviso «${label}»` : null
}

/** Los destinatarios, si la entrada los trae. Array de correos escrito por el handler. */
function recipientsOf(meta: Record<string, unknown>): string[] {
  return Array.isArray(meta.recipients)
    ? (meta.recipients as unknown[]).map(String).filter(Boolean)
    : []
}

/**
 * DE DÓNDE salió el aviso: la etapa, y qué lo disparó.
 *
 * Es la mitad de la pregunta que R2b promete poder contestar («¿por qué a este le llegó ESE
 * correo?»); la otra mitad son los destinatarios. `origin` sólo viene cuando el aviso NO nace de un
 * ítem —lo declara el escritor—, así que su ausencia significa «lo disparó una marca», que ya se
 * entiende con la etapa sola.
 */
function noticeOriginDetail(
  meta: Record<string, unknown>,
  labels: AuditLabelResolver
): string | undefined {
  const parts: string[] = []

  // ☠️ ACÁ SE FILTRABA LA CLAVE. El fallback era `text(meta.stageKey)`, y como NINGUNA entrada
  // anterior al sello trae `stageLabel`, el renglón terminaba en «Etapa initial_contact». Una
  // etapa que ni el sello ni el expediente saben nombrar se OMITE: el resto del detalle («al
  // abrir la etapa») se lee perfecto sin ella, y nombrarla «Etapa una etapa» no es una frase.
  const stage = stageName(meta, labels)
  if (stage) parts.push(`Etapa ${stage}`)

  // El punto del que colgaba el aviso — el dato que el nombre del aviso NO puede dar sin mentir.
  // Sólo aporta cuando el aviso NO pudo nombrarse: si el sello trajo `itemLabel`, el predicado ya
  // lo dijo y repetirlo es ruido. Y un aviso de ETAPA no cuelga de ningún punto: `itemKey` viene
  // vacío justamente por eso.
  const item = meta.origin === 'stage' || text(meta.itemLabel) ? null : itemName(meta, labels)
  if (item) parts.push(`Punto ${item}`)

  if (meta.origin === 'stage') {
    parts.push(meta.stageMoment === 'exit' ? 'al cerrar la etapa' : 'al abrir la etapa')
  } else if (meta.origin === 'rule') {
    parts.push(noticeRuleClause(meta, labels))
  }

  return parts.length ? parts.join(' · ') : undefined
}

/**
 * La regla que eligió el aviso, en castellano.
 *
 * Los DOS lados de la comparación son claves: la izquierda es un token de variable del kernel
 * (`etapa4.resolutionResult`) y la derecha la clave de una opción (`in_review`). Sin diccionario
 * el renglón decía «por la regla etapa4.resolutionResult = in_review», que no le dice nada a
 * nadie; con él dice «por la regla Resultado de la resolución = En revisión». Si la variable no
 * resuelve se declara la existencia de la regla sin deletrearla: que hubo una condición es cierto,
 * y cuál era no se sabe.
 */
function noticeRuleClause(meta: Record<string, unknown>, labels: AuditLabelResolver): string {
  const variable = text(meta.variable)
  const field = variable ? labels.field?.(variable) : undefined
  if (!variable || !field) return 'por una regla del proceso'

  const equals = text(meta.equals)
  if (!equals) return `por la regla ${field}`
  return `por la regla ${field} = ${labels.option?.(equals, variable) ?? equals}`
}

/**
 * Por qué falló un aviso, en español.
 *
 * ⚠️ Se compara el CÓDIGO, nunca el mensaje: los códigos son contrato y el texto es traducción. Lo
 * que no está en el mapa se muestra crudo — es el mensaje del error real (un timeout de SMTP, una
 * plantilla rota) y esconderlo dejaría a la bitácora diciendo «falló» sin decir por qué.
 */
const NOTICE_FAILURE_REASONS: Record<string, string> = {
  CREDIT_NOTIFY_CLIENT_FORBIDDEN:
    'El aviso iba al cliente y la cuenta que lo disparó no tiene el permiso para notificarlo.'
}

const noticeFailureReason = (reason: unknown): string => {
  const code = text(reason)
  if (!code) return 'Sin motivo registrado.'
  return NOTICE_FAILURE_REASONS[code] ?? code
}

/**
 * Por qué un aviso automático no salió. Mismo criterio: código adentro, texto afuera.
 *
 * ⚠️ **Este mapa es un CATÁLOGO CERRADO, y hoy está completo.** Los motivos de salto los escribe
 * nuestro propio kernel, nunca un tercero: son exactamente dos —`AUTO_NOTIFY_OFF`
 * (`credit-auto-start.service.ts`) y `CLIENT_NOTICES_OFF` (`credit-notice-sender.ts`, que lo toma
 * de la constante única en `credits/domain/client-audience.ts`)—. Si el kernel suma un tercero,
 * acá va su traducción **el mismo día**.
 */
const NOTICE_SKIP_REASONS: Record<string, string> = {
  AUTO_NOTIFY_OFF: 'Los avisos automáticos están apagados en la plantilla del proceso.',
  // ☠️ El interruptor NO apaga el aviso: descarta a los destinatarios de cliente (`client`,
  // `principalOwner`, `allOwners`) y deja salir el correo al asesor y al equipo. Decir «no se
  // envió el aviso» a secas sería falso, y este renglón es la ÚNICA huella de que al comprador no
  // le llegó nada — por eso la frase nombra las dos mitades del hecho.
  CLIENT_NOTICES_OFF:
    'Los avisos al cliente están desactivados en la configuración de la plataforma: el correo salió al equipo interno, pero no al comprador.'
}

/**
 * ☠️ **Un motivo desconocido NO se deletrea** — al revés que en `noticeFailureReason`.
 *
 * No es una inconsistencia: son dos cosas distintas. El motivo de un FALLO puede ser el mensaje
 * real del error (`connect ETIMEDOUT 10.0.0.1:587`), y esconderlo dejaría a la bitácora diciendo
 * «falló» sin decir por qué. El motivo de un SALTO, en cambio, siempre es un CÓDIGO nuestro de un
 * catálogo cerrado: mostrarlo crudo sería enseñar una constante de la plataforma, y en esta pantalla
 * eso no pasa. La frase genérica igual afirma lo único cierto — que hubo un motivo registrado.
 */
const noticeSkipReason = (reason: unknown): string | undefined => {
  const code = text(reason)
  if (!code) return undefined
  return (
    NOTICE_SKIP_REASONS[code] ?? 'El aviso se omitió por una regla de configuración del proceso.'
  )
}

/** Predicado (frase verbal sin sujeto) + detalle + tarjetas, según la acción. */
type Predicate = { predicate: string; subjectless?: boolean; detail?: string; cards?: AuditCard[] }

/** Fallback: convierte una acción desconocida en algo legible ("hizo: negotiation.updated"). */
const FALLBACK_PREDICATES: Record<string, string> = {
  'client.created': 'creó el cliente',
  'client.updated': 'actualizó el cliente',
  'client.deleted': 'eliminó el cliente',
  'negotiation.created': 'creó la negociación',
  'negotiation.updated': 'actualizó la negociación',
  'negotiation.state_changed': 'cambió el estado de la negociación',
  'negotiation.activated': 'activó la negociación',
  'negotiation.deactivated': 'desactivó la negociación',
  'negotiation.rescinded': 'rescindió la negociación',
  'negotiation.rejected': 'rechazó la negociación',
  'negotiation.deleted': 'eliminó la negociación',
  'user.created': 'creó el usuario',
  'user.updated': 'actualizó el usuario',
  'user.deleted': 'eliminó el usuario'
}

function buildPredicate(entry: AuditLogEntry, labels: AuditLabelResolver): Predicate {
  const meta = entry.metadata ?? {}

  switch (entry.action) {
    case 'portal.created':
      return { predicate: 'creó la solicitud de validación' }
    case 'portal.sent':
      return { predicate: 'envió el enlace al cliente' }
    case 'portal.email_opened':
      return { predicate: 'abrió el correo' }
    case 'portal.opened':
      return { predicate: 'abrió el formulario' }

    case 'portal.section_saved':
      return { predicate: `guardó ${sectionLabel(meta.section)}` }

    case 'portal.submitted':
      return { predicate: 'envió su información' }
    case 'portal.resubmitted':
      return { predicate: 'reenvió su información' }

    case 'portal.review_taken_over':
      return { predicate: 'trajo el expediente a revisión' }
    case 'portal.review_started':
      return { predicate: 'comenzó la revisión' }

    case 'portal.fields_reviewed': {
      const fields = Array.isArray(meta.fields) ? (meta.fields as string[]) : []
      const n = fields.length
      // El CONTEO sale de la entrada; la lista, sólo de lo que se sabe nombrar. Un campo que el
      // catálogo no describe se cae de la enumeración en vez de aparecer como dot-path: el
      // renglón sigue diciendo cuántos fueron, que es el dato que no se puede perder.
      const named = fields.map(reviewFieldLabel).filter((label): label is string => label !== null)
      return {
        predicate: `revisó ${n} ${n === 1 ? 'campo' : 'campos'}`,
        detail: named.length ? named.join(', ') : undefined
      }
    }

    case 'portal.field_edited':
      return {
        predicate: `corrigió ${portalFieldLabel(typeof meta.field === 'string' ? meta.field : '') ?? 'un campo'}`,
        detail: `${stringify(meta.previousValue)} → ${stringify(meta.newValue)}`
      }

    case 'portal.changes_requested': {
      const corrections = (meta.corrections as Correction[] | undefined) ?? []
      const n = corrections.length
      const cards: AuditCard[] = []
      if (typeof meta.comment === 'string' && meta.comment.trim()) {
        cards.push({ title: 'Nota general', body: meta.comment.trim(), tone: 'warning' })
      }
      for (const c of corrections) {
        const note = typeof c.comment === 'string' ? c.comment.trim() : ''
        if (!note) continue
        cards.push({ title: correctionLabel(c), body: note, tone: 'warning' })
      }
      return {
        predicate: `solicitó ${n || ''} ${n === 1 ? 'corrección' : 'correcciones'}`.replace(
          '  ',
          ' '
        ),
        cards: cards.length ? cards : undefined
      }
    }

    case 'portal.client_comment':
      return {
        predicate: 'dejó un comentario',
        cards:
          typeof meta.comment === 'string' && meta.comment.trim()
            ? [{ body: meta.comment.trim(), tone: 'info' }]
            : undefined
      }

    case 'portal.approved':
      return { predicate: 'aprobó el expediente' }

    case 'portal.rejected':
      return {
        predicate: 'rechazó el expediente',
        cards:
          typeof meta.reason === 'string' && meta.reason.trim()
            ? [{ title: 'Motivo', body: meta.reason.trim(), tone: 'danger' }]
            : undefined
      }

    case 'portal.revoked':
      return {
        predicate: 'canceló el flujo',
        cards:
          typeof meta.reason === 'string' && meta.reason.trim()
            ? [{ title: 'Motivo', body: meta.reason.trim(), tone: 'danger' }]
            : undefined
      }

    case 'portal.reminder_sent':
      return {
        predicate: 'envió un recordatorio',
        detail:
          meta.reminderCount != null
            ? `Recordatorio n.º ${stringify(meta.reminderCount)}`
            : undefined
      }

    case 'portal.link_extended':
      return {
        predicate: 'renovó el enlace',
        detail:
          meta.expirationDays != null
            ? `Nueva vigencia: ${stringify(meta.expirationDays)} días`
            : undefined
      }

    case 'portal.expired':
      return { predicate: 'El enlace expiró', subjectless: true }

    case 'credit.started': {
      // Los DOS caminos de R10 se leen distinto porque SON distintos: uno lo decidió una persona y
      // el otro lo disparó el reloj. «Sistema inició el expediente» no lo decía — se leía como si
      // hubiera una cuenta llamada Sistema operando la pantalla.
      const automatic = meta.autoStarted === true || entry.actor.type === 'system'
      const detail = creditStartDetail(meta)
      if (automatic) {
        return {
          predicate:
            'El expediente se abrió automáticamente, por el disparo programado del sistema',
          subjectless: true,
          detail
        }
      }
      return { predicate: 'abrió el expediente', detail }
    }

    case 'credit.item_checked': {
      const item = itemName(meta, labels) ?? 'un punto del checklist'
      return {
        predicate: meta.checked === false ? `desmarcó ${item}` : `marcó ${item}`,
        cards:
          typeof meta.note === 'string' && meta.note.trim()
            ? [{ title: 'Nota', body: meta.note.trim(), tone: 'neutral' }]
            : undefined
      }
    }

    case 'credit.field_answered': {
      const field = fieldName(meta, labels) ?? 'un campo'
      return { predicate: `respondió ${field}`, detail: answerText(meta, labels) }
    }

    case 'credit.stage_advanced': {
      const stage = stageName(meta, labels) ?? 'una etapa'
      const next = stageName(meta, labels, 'nextStageLabel', 'nextStageKey')

      // ☠️ EL CIERRE DEL PROCESO SE DECIDE POR LO QUE SELLÓ EL ESCRITOR, NO POR LA ETIQUETA.
      // Antes el detalle era `next ? … : 'Proceso completado'`, y como NINGUNA entrada anterior al
      // sello trae `nextStageLabel`, TODOS los avances intermedios de la base decían «Proceso
      // completado» teniendo `nextStageKey` apuntando a la etapa siguiente. Un renglón de bitácora
      // que afirma lo contrario de lo que pasó es peor que un renglón sin detalle.
      const finished =
        meta.completed === true ||
        (meta.completed !== false && 'nextStageKey' in meta && meta.nextStageKey == null)

      return {
        predicate: `cerró ${stage}`,
        detail: finished ? 'Proceso completado' : next ? `Siguiente: ${next}` : undefined
      }
    }

    case 'credit.financing_type_set': {
      // ☠️ La clave es `to`, NO `financingType`. El escritor real
      // (`change-route-decision.use-case.ts`) sella el «de qué a qué» —`from`/`to`— y esto leía una
      // clave que nadie escribe: `undefined === 'cash'` es `false`, así que TODA corrección a
      // Contado se mostraba como «definió la modalidad como Crédito». Un renglón de bitácora que
      // afirma lo contrario de lo que pasó es peor que no tener el renglón.
      const applied = financingTypeLabel(meta.to ?? meta.financingType)
      const previous = financingTypeLabel(meta.from)
      return {
        // Sin una modalidad conocida NO se interpola: `${null}` imprimía «definió la modalidad
        // como null», y una `to` de un catálogo futuro imprimiría su clave.
        predicate: applied
          ? `definió la modalidad como ${applied}`
          : 'actualizó la modalidad del expediente',
        detail: previous ? `Antes: ${previous}` : undefined,
        cards:
          typeof meta.reason === 'string' && meta.reason.trim()
            ? [{ title: 'Motivo del cambio', body: meta.reason.trim(), tone: 'info' }]
            : undefined
      }
    }

    case 'credit.approved':
      return { predicate: 'aprobó el crédito' }

    case 'credit.notice_skipped':
      return {
        predicate: 'no envió el aviso automático',
        detail: noticeSkipReason(meta.reason)
      }

    case 'credit.notice_sent': {
      const notice = noticeName(meta)
      const to = recipientsOf(meta)
      // Sin destinatarios no hubo correo: el escritor lo marca `empty` y decir «envió» sería falso.
      if (meta.empty === true || to.length === 0) {
        return {
          predicate: notice
            ? `no envió ${notice}: no había destinatarios`
            : 'no envió el aviso: no había destinatarios',
          detail: noticeOriginDetail(meta, labels)
        }
      }
      return {
        predicate: `envió ${notice ?? 'un aviso'} a ${to.length} ${to.length === 1 ? 'destinatario' : 'destinatarios'}`,
        detail: noticeOriginDetail(meta, labels),
        cards: [
          {
            title: to.length === 1 ? 'Destinatario' : 'Destinatarios',
            body: to.join(', '),
            tone: 'info'
          }
        ]
      }
    }

    case 'credit.notice_failed': {
      const notice = noticeName(meta)
      const to = recipientsOf(meta)
      return {
        predicate: notice ? `no pudo enviar ${notice}` : 'intentó enviar un aviso',
        detail: noticeOriginDetail(meta, labels),
        cards: [
          { title: 'No se pudo enviar', body: noticeFailureReason(meta.reason), tone: 'danger' },
          ...(to.length
            ? [
                {
                  title: to.length === 1 ? 'Destinatario' : 'Destinatarios',
                  body: to.join(', '),
                  tone: 'neutral' as const
                }
              ]
            : [])
        ]
      }
    }

    default:
      // ☠️ Una acción sin traducción NO se deletrea: `registró: credit.notice_failed` es el
      // namespace interno de la plataforma en la cara del usuario. El renglón dice que hubo un
      // movimiento —que es lo único cierto que se sabe— y el `label` ya lo tituló «Actividad».
      return { predicate: FALLBACK_PREDICATES[entry.action] ?? 'registró una acción' }
  }
}

/**
 * Convierte una entrada de la bitácora en algo mostrable: un predicado (frase verbal que el
 * timeline teje con el nombre del actor en negrita), detalle opcional, tarjetas de metadata y
 * el actor base. El nombre del actor sale del backend si está sellado; si no, se resuelve por
 * id (`resolveActor`); el cliente lo sobreescribe el timeline con su identidad real.
 *
 * `labels` hace por las CLAVES lo que `resolveActor` hace por los ids: traduce contra el
 * expediente abierto lo que la entrada no trae sellado. Es opcional — sin él la bitácora se lee
 * más genérica («una etapa»), pero jamás muestra una clave.
 */
export function humanizeAuditEntry(
  entry: AuditLogEntry,
  resolveActor: (id: string) => string,
  labels: AuditLabelResolver = NO_LABELS
): HumanAuditEntry {
  const actorType =
    entry.actor.type === 'client' ? 'client' : entry.actor.type === 'system' ? 'system' : 'internal'

  const actor = entry.actor.name?.trim()
    ? entry.actor.name
    : actorType === 'client'
      ? 'Cliente'
      : actorType === 'system'
        ? 'Sistema'
        : entry.actor.id
          ? resolveActor(entry.actor.id)
          : 'Equipo interno'

  const { predicate, subjectless, detail, cards } = buildPredicate(entry, labels)

  return {
    label: LABELS[entry.action] ?? 'Actividad',
    predicate,
    subjectless,
    detail,
    cards,
    actor,
    actorType,
    occurredAt: entry.occurredAt
  }
}
